import React, { useState, useEffect } from "react";
import { Icon } from "../icons.jsx";
import {
  Card, Button, Badge, Field, Check, Radio, Switch, Segmented, Modal,
  Donut, MultiDonut, BarChart, Slider,
} from "../components.jsx";
import { useStore } from "../store.jsx";
import { TALENTION_HELPERS } from "../data.js";

import { PageHead } from "./dashboard.jsx";

/* ============================================================
   Talention — Usuarios y Roles
   ============================================================ */

const MODULES = [
  { key: "dashboard",   label: "Dashboard",         icon: "dashboard" },
  { key: "entity",      label: "Perfil de Entidad", icon: "building" },
  { key: "departments", label: "Departamentos",     icon: "users" },
  { key: "levels",      label: "Niveles",           icon: "tree" },
  { key: "positions",   label: "Puestos",           icon: "briefcase" },
  { key: "factors",     label: "Factores",          icon: "sliders" },
  { key: "scales",      label: "Escalas",           icon: "scale" },
  { key: "valuation",   label: "Valorar Puestos",   icon: "check-list" },
  { key: "users",       label: "Usuarios",          icon: "users" },
  { key: "roles",       label: "Roles y permisos",  icon: "lock" },
];

const PERM_LABELS = {
  view: "Ver",
  edit: "Editar",
  finalize: "Finalizar",
};

const AVATAR_BG = ["#0a84ff", "#34c759", "#af52de", "#ff9500", "#ff3b30", "#5856d6", "#06b6d4"];

function initialsFromName(name) {
  return name.split(" ").filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("");
}

/* ============================================================
   USUARIOS
   ============================================================ */

function Users() {
  const { state, dispatch } = useStore();
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const filtered = state.users.filter(u => {
    if (filterRole !== "all" && u.roleId !== parseInt(filterRole)) return false;
    if (filterStatus !== "all" && u.status !== filterStatus) return false;
    if (query && !u.name.toLowerCase().includes(query.toLowerCase()) && !u.email.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const total = state.users.length;
  const active = state.users.filter(u => u.status === "ACTIVO").length;
  const invited = state.users.filter(u => u.status === "INVITADO").length;
  const suspended = state.users.filter(u => u.status === "SUSPENDIDO").length;

  return (
    <div className="stack stagger" style={{ gap: 22 }}>
      <PageHead
        eyebrow="Administración"
        title="Usuarios"
        sub="Personas con acceso a esta instancia. Asigna un rol a cada uno para definir qué pueden hacer."
        actions={
          <div className="row">
            <button className="btn btn-secondary"><Icon name="download" size={14} /> Exportar</button>
            <button className="btn btn-primary" onClick={() => setEditing("new")}>
              <Icon name="plus" size={14} /> Invitar usuario
            </button>
          </div>
        }
      />

      <div className="grid grid-4 kpi-grid">
        <KPIMini label="Usuarios totales" value={total} icon="users" color="#0a84ff" />
        <KPIMini label="Activos" value={active} icon="circle-check" color="#34c759" />
        <KPIMini label="Invitados" value={invited} icon="mail" color="#af52de" />
        <KPIMini label="Suspendidos" value={suspended} icon="lock" color="#9aa1ad" />
      </div>

      <div className="card flush" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{
          padding: "13px 20px", background: "var(--bg-soft)", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "var(--ink-900)", color: "white", display: "grid", placeItems: "center" }}>
              <Icon name="users" size={14} />
            </div>
            <span style={{ fontWeight: 650, fontSize: 13.5, letterSpacing: "-0.015em" }}>Directorio de usuarios</span>
          </div>
          <span style={{ fontSize: 11.5, color: "var(--ink-400)" }}>{state.users.length} usuarios</span>
        </div>
        <div style={{ padding: "12px 18px", display: "flex", gap: 10, alignItems: "center", borderBottom: "1px solid var(--border)" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, flex: 1, maxWidth: 320,
            padding: "7px 12px", borderRadius: 10, background: "white", border: "1px solid var(--border)",
          }}>
            <Icon name="search" size={14} className="muted" />
            <input value={query} onChange={e => setQuery(e.target.value)}
                   placeholder="Buscar por nombre o email..." style={{ border: "none", outline: "none", background: "transparent", fontSize: 13, flex: 1 }} />
          </div>
          <select className="select" value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ width: "auto" }}>
            <option value="all">Todos los roles</option>
            {state.roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <Segmented
            value={filterStatus}
            onChange={setFilterStatus}
            options={[
              { value: "all", label: "Todos" },
              { value: "ACTIVO", label: "Activos" },
              { value: "INVITADO", label: "Invitados" },
              { value: "SUSPENDIDO", label: "Suspendidos" },
            ]}
          />
          <span className="muted tiny" style={{ marginLeft: "auto" }}>{filtered.length} de {total}</span>
        </div>

        <table className="t-table">
          <thead><tr>
            <th>Usuario</th>
            <th>Rol</th>
            <th>Departamento</th>
            <th>Estado</th>
            <th>Último acceso</th>
            <th style={{ width: 110 }}>Acciones</th>
          </tr></thead>
          <tbody>
            {filtered.map(u => {
              const role = state.roles.find(r => r.id === u.roleId);
              const dept = state.departments.find(d => d.id === u.departmentId);
              return (
                <tr key={u.id}>
                  <td>
                    <div className="row" style={{ gap: 12 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: "50%",
                        background: AVATAR_BG[u.id % AVATAR_BG.length],
                        color: "white", display: "grid", placeItems: "center",
                        fontWeight: 700, fontSize: 12, letterSpacing: "-0.01em",
                        flexShrink: 0,
                      }}>{initialsFromName(u.name)}</div>
                      <div style={{ minWidth: 0 }}>
                        <div className="semi" style={{ fontSize: 14, letterSpacing: "-0.01em" }}>
                          {u.name}{u.external && <span style={{ marginLeft: 6, fontSize: 10.5, padding: "1px 6px", borderRadius: 999, background: "var(--ink-150)", color: "var(--ink-600)", fontWeight: 600 }}>EXTERNO</span>}
                        </div>
                        <div className="muted tiny">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {role && (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "3px 9px", borderRadius: 999,
                        background: role.color + "1a", color: role.color,
                        fontSize: 12, fontWeight: 600, border: "1px solid " + role.color + "33",
                      }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: role.color }} />
                        {role.name}
                      </span>
                    )}
                  </td>
                  <td>{dept?.name || <span className="muted">—</span>}</td>
                  <td>
                    <UserStatus status={u.status} />
                  </td>
                  <td className="muted tiny">{u.lastLogin}</td>
                  <td>
                    <div className="row" style={{ gap: 4, justifyContent: "flex-end" }}>
                      <button className="icon-btn" onClick={() => setEditing(u)} title="Editar"><Icon name="edit" size={13} /></button>
                      <button className="icon-btn" onClick={() => {
                        if (confirm(`¿Eliminar el usuario "${u.name}"?`)) dispatch({ type: "DELETE_USER", id: u.id });
                      }} title="Eliminar"><Icon name="trash" size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="6" className="empty">No se encontraron usuarios</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <UserModal
        open={editing !== null}
        user={editing === "new" ? null : editing}
        roles={state.roles}
        departments={state.departments}
        onClose={() => setEditing(null)}
        onSave={(data) => {
          if (editing === "new") dispatch({ type: "ADD_USER", user: { ...data, status: "INVITADO" } });
          else dispatch({ type: "UPDATE_USER", id: editing.id, patch: data });
          setEditing(null);
        }}
      />
    </div>
  );
}

function UserStatus({ status }) {
  const tone = status === "ACTIVO" ? { color: "#34c759", label: "Activo" }
    : status === "INVITADO" ? { color: "#af52de", label: "Invitado" }
    : status === "SUSPENDIDO" ? { color: "#ff3b30", label: "Suspendido" }
    : { color: "#9aa1ad", label: status };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "3px 10px", borderRadius: 999,
      background: "white", border: "1px solid var(--border)",
      fontSize: 12, fontWeight: 590, color: "var(--ink-800)",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: tone.color }} />
      {tone.label}
    </span>
  );
}

function UserModal({ open, user, roles, departments, onClose, onSave }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState(roles[0]?.id || 0);
  const [departmentId, setDepartmentId] = useState("");
  const [status, setStatus] = useState("ACTIVO");
  useEffect(() => {
    setName(user?.name || "");
    setEmail(user?.email || "");
    setRoleId(user?.roleId || roles[0]?.id || 0);
    setDepartmentId(user?.departmentId || "");
    setStatus(user?.status || "ACTIVO");
  }, [open, user]);
  const role = roles.find(r => r.id === parseInt(roleId));

  return (
    <Modal open={open} onClose={onClose} title={user ? "Editar usuario" : "Invitar usuario"}
      width={580}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={() => {
            if (!name.trim() || !email.trim()) return;
            const patch = { name, email, roleId: parseInt(roleId), departmentId: departmentId ? parseInt(departmentId) : null };
            if (user) patch.status = status;
            onSave(patch);
          }}>
            {user ? "Guardar cambios" : "Enviar invitación"}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <div className="grid grid-2">
          <Field label="Nombre completo">
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Ana García" autoFocus />
          </Field>
          <Field label="Email" hint={user ? "" : "Le enviaremos una invitación a esta dirección"}>
            <input type="email" className="input" value={email} onChange={e => setEmail(e.target.value)} placeholder="ana@empresa.com" />
          </Field>
        </div>
        <Field label="Rol asignado" hint={role?.description}>
          <select className="select" value={roleId} onChange={e => setRoleId(e.target.value)}>
            {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </Field>
        <Field label="Departamento (opcional)">
          <select className="select" value={departmentId} onChange={e => setDepartmentId(e.target.value)}>
            <option value="">Sin asignar</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        {user && (
          <Field label="Estado">
            <Segmented
              value={status}
              onChange={setStatus}
              options={[
                { value: "ACTIVO", label: "Activo" },
                { value: "INVITADO", label: "Invitado" },
                { value: "SUSPENDIDO", label: "Suspendido" },
              ]}
            />
          </Field>
        )}

        {role && (
          <div className="surface" style={{ padding: 14, marginTop: 4 }}>
            <div className="card-eyebrow" style={{ marginBottom: 8 }}>Vista previa de permisos</div>
            <div className="row wrap" style={{ gap: 6 }}>
              {MODULES.map(m => {
                const perm = role.permissions[m.key];
                const hasAny = perm && (perm.view || perm.edit);
                return (
                  <span key={m.key} style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    padding: "4px 9px", borderRadius: 999,
                    background: hasAny ? "var(--good-100)" : "var(--ink-150)",
                    color: hasAny ? "#15803d" : "var(--ink-500)",
                    fontSize: 11.5, fontWeight: 590,
                  }}>
                    <Icon name={hasAny ? "check" : "x"} size={10} />
                    {m.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function KPIMini({ label, value, icon, color }) {
  const c = color || "var(--accent)";
  const tint = color ? color + "18" : "var(--accent-light)";
  return (
    <div className="kpi-card kpi-card-mini" style={{ "--kpi-color": c }}>
      <div className="kpi-head">
        <span className="kpi-label">{label}</span>
        <span className="kpi-icon" style={{ background: tint, color: c }}>
          <Icon name={icon} size={14} />
        </span>
      </div>
      <div className="kpi-num">{value}</div>
    </div>
  );
}

/* ============================================================
   ROLES
   ============================================================ */

function Roles() {
  const { state, dispatch } = useStore();
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(state.roles[0]?.id);

  const role = state.roles.find(r => r.id === selected) || state.roles[0];
  const userCount = role ? state.users.filter(u => u.roleId === role.id).length : 0;

  return (
    <div className="stack stagger" style={{ gap: 22 }}>
      <PageHead
        eyebrow="Administración"
        title="Roles y permisos"
        sub="Controla qué puede hacer cada usuario. Los roles agrupan permisos por módulo."
        actions={
          <button className="btn btn-primary" onClick={() => setEditing("new")}>
            <Icon name="plus" size={14} /> Nuevo rol
          </button>
        }
      />

      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 18 }}>
        {/* Lista de roles */}
        <div className="stack" style={{ gap: 8 }}>
          {state.roles.map(r => {
            const isActive = r.id === role?.id;
            const count = state.users.filter(u => u.roleId === r.id).length;
            return (
              <button key={r.id} onClick={() => setSelected(r.id)}
                style={{
                  textAlign: "left", cursor: "pointer",
                  background: isActive ? "white" : "rgba(255,255,255,0.55)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                  borderRadius: 14,
                  padding: "14px 16px",
                  border: "1px solid " + (isActive ? "var(--ink-900)" : "rgba(255,255,255,0.6)"),
                  boxShadow: isActive ? "0 4px 12px -6px rgba(15,18,26,0.2)" : "0 1px 2px rgba(15,18,26,0.04)",
                  display: "flex", gap: 12, alignItems: "center",
                  transition: "border-color 120ms, background 120ms",
                }}>
                <span style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: r.color + "1f", color: r.color,
                  display: "grid", placeItems: "center", flexShrink: 0,
                }}><Icon name="shield" size={16} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="row" style={{ gap: 6 }}>
                    <span className="semi" style={{ fontSize: 14, letterSpacing: "-0.01em" }}>{r.name}</span>
                    {r.builtIn && (
                      <span style={{ fontSize: 9.5, padding: "1px 6px", borderRadius: 999, background: "var(--ink-150)", color: "var(--ink-600)", fontWeight: 700, letterSpacing: 0.04 + "em" }}>
                        SISTEMA
                      </span>
                    )}
                  </div>
                  <div className="muted tiny" style={{ marginTop: 2 }}>
                    {count} usuario{count !== 1 ? "s" : ""}
                  </div>
                </div>
                <Icon name="chevron-right" size={14} className="muted" />
              </button>
            );
          })}
        </div>

        {/* Detalle del rol seleccionado */}
        {role && (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            {/* Header */}
            <div style={{
              padding: "22px 24px 20px",
              borderBottom: "1px solid var(--border)",
              display: "flex", gap: 16, alignItems: "flex-start",
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 14,
                background: role.color + "1f", color: role.color,
                display: "grid", placeItems: "center", flexShrink: 0,
              }}><Icon name="shield" size={20} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="row" style={{ gap: 8 }}>
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: "-0.025em" }}>{role.name}</h2>
                  {role.builtIn && (
                    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 999, background: "var(--ink-150)", color: "var(--ink-600)", fontWeight: 700, letterSpacing: 0.04 + "em" }}>
                      ROL DEL SISTEMA
                    </span>
                  )}
                </div>
                <div className="muted" style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>{role.description}</div>
                <div className="row" style={{ gap: 8, marginTop: 12 }}>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "3px 10px", borderRadius: 999, background: "var(--ink-100)",
                    fontSize: 12, fontWeight: 590,
                  }}>
                    <Icon name="users" size={11} />
                    {userCount} usuario{userCount !== 1 ? "s" : ""}
                  </span>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "3px 10px", borderRadius: 999, background: "var(--ink-100)",
                    fontSize: 12, fontWeight: 590,
                  }}>
                    <Icon name="check-list" size={11} />
                    {Object.values(role.permissions).filter(p => p.view).length} módulos con acceso
                  </span>
                </div>
              </div>
              <div className="row" style={{ gap: 6, flexShrink: 0 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditing(role)}>
                  <Icon name="edit" size={13} /> Editar
                </button>
                {!role.builtIn && (
                  <button className="btn btn-ghost btn-sm" onClick={() => {
                    if (userCount > 0) {
                      alert("Este rol tiene usuarios asignados. Reasígnalos antes de eliminar.");
                      return;
                    }
                    if (confirm(`¿Eliminar el rol "${role.name}"?`)) {
                      dispatch({ type: "DELETE_ROLE", id: role.id });
                      setSelected(state.roles[0]?.id);
                    }
                  }}>
                    <Icon name="trash" size={13} />
                  </button>
                )}
              </div>
            </div>

            {/* Matriz de permisos */}
            <div style={{ padding: "20px 24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--accent)", flexShrink: 0 }} />
                <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--ink-400)" }}>Permisos por módulo</span>
              </div>
              <div className="stack" style={{ gap: 4 }}>
                {MODULES.map(m => {
                  const perm = role.permissions[m.key] || {};
                  const keys = Object.keys(perm);
                  const granted = keys.filter(k => perm[k]);
                  const all = granted.length === keys.length && keys.length > 0;
                  const none = granted.length === 0;
                  return (
                    <div key={m.key} style={{
                      display: "flex", alignItems: "center", gap: 14,
                      padding: "10px 14px",
                      borderRadius: 12,
                      background: none ? "rgba(15,18,26,0.025)" : "white",
                      border: "1px solid var(--border)",
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 9,
                        background: none ? "var(--ink-150)" : all ? "var(--good-100)" : "var(--info-100)",
                        color: none ? "var(--ink-500)" : all ? "#15803d" : "#0a52c2",
                        display: "grid", placeItems: "center", flexShrink: 0,
                      }}><Icon name={m.icon} size={14} /></div>
                      <span className="semi" style={{ fontSize: 13.5, flex: 1, letterSpacing: "-0.005em" }}>{m.label}</span>
                      <div className="row" style={{ gap: 6 }}>
                        {keys.map(k => (
                          <span key={k} style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            padding: "3px 9px", borderRadius: 999,
                            background: perm[k] ? "var(--good-100)" : "var(--ink-150)",
                            color: perm[k] ? "#15803d" : "var(--ink-500)",
                            fontSize: 11.5, fontWeight: 590,
                          }}>
                            <Icon name={perm[k] ? "check" : "x"} size={10} />
                            {PERM_LABELS[k] || k}
                          </span>
                        ))}
                        {keys.length === 0 && <span className="muted tiny">Sin permisos</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <RoleModal
        open={editing !== null}
        role={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSave={(data) => {
          if (editing === "new") dispatch({ type: "ADD_ROLE", role: data });
          else dispatch({ type: "UPDATE_ROLE", id: editing.id, patch: data });
          setEditing(null);
        }}
      />
    </div>
  );
}

function RoleModal({ open, role, onClose, onSave }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#0a84ff");
  const [permissions, setPermissions] = useState({});

  const defaultPerms = () => {
    const p = {};
    MODULES.forEach(m => {
      p[m.key] = m.key === "valuation"
        ? { view: false, edit: false, finalize: false }
        : { view: false, edit: false };
    });
    return p;
  };

  useEffect(() => {
    setName(role?.name || "");
    setDescription(role?.description || "");
    setColor(role?.color || "#0a84ff");
    setPermissions(role?.permissions ? JSON.parse(JSON.stringify(role.permissions)) : defaultPerms());
  }, [open, role]);

  const togglePerm = (mod, key) => {
    setPermissions(prev => {
      const next = { ...prev, [mod]: { ...(prev[mod] || {}) } };
      next[mod][key] = !next[mod][key];
      // view-off should imply edit/finalize off
      if (key === "view" && !next[mod].view) {
        if ("edit" in next[mod]) next[mod].edit = false;
        if ("finalize" in next[mod]) next[mod].finalize = false;
      }
      return next;
    });
  };

  const presetColors = ["#0a84ff", "#34c759", "#af52de", "#ff9500", "#ff3b30", "#5856d6", "#06b6d4", "#1d1d1f"];

  return (
    <Modal open={open} onClose={onClose} title={role ? "Editar rol" : "Nuevo rol"}
      width={680}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={() => {
            if (!name.trim()) return;
            onSave({ name, description, color, permissions });
          }}>
            {role ? "Guardar" : "Crear rol"}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <div className="grid grid-2">
          <Field label="Nombre del rol">
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Ej. Coordinador" autoFocus />
          </Field>
          <Field label="Color identificativo">
            <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
              {presetColors.map(c => (
                <button key={c} onClick={() => setColor(c)}
                  style={{
                    width: 28, height: 28, borderRadius: 8, background: c,
                    border: color === c ? "2px solid var(--ink-900)" : "1px solid var(--border)",
                    cursor: "pointer", padding: 0,
                  }} />
              ))}
            </div>
          </Field>
        </div>
        <Field label="Descripción" hint="¿Qué hace este rol? Visible para administradores al asignarlo.">
          <textarea className="textarea" rows={2} value={description} onChange={e => setDescription(e.target.value)} placeholder="Descripción del rol y sus responsabilidades..." />
        </Field>

        <div>
          <div className="card-eyebrow" style={{ marginBottom: 8 }}>Permisos por módulo</div>
          <div className="surface" style={{ padding: 8 }}>
            {MODULES.map(m => {
              const perm = permissions[m.key] || {};
              const keys = m.key === "valuation" ? ["view", "edit", "finalize"] : ["view", "edit"];
              return (
                <div key={m.key} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "8px 10px",
                  borderBottom: m.key === MODULES[MODULES.length - 1].key ? "none" : "1px solid var(--border)",
                }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: 7,
                    background: "var(--ink-100)", color: "var(--ink-700)",
                    display: "grid", placeItems: "center", flexShrink: 0,
                  }}><Icon name={m.icon} size={12} /></div>
                  <span className="semi" style={{ fontSize: 13, flex: 1 }}>{m.label}</span>
                  {keys.map(k => (
                    <label key={k} style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      cursor: "pointer", userSelect: "none",
                      padding: "4px 9px", borderRadius: 999,
                      background: perm[k] ? "var(--good-100)" : "transparent",
                      color: perm[k] ? "#15803d" : "var(--ink-500)",
                      fontSize: 11.5, fontWeight: 590,
                      border: "1px solid " + (perm[k] ? "transparent" : "var(--border)"),
                      transition: "all 100ms",
                    }} onClick={() => togglePerm(m.key, k)}>
                      <Icon name={perm[k] ? "check" : "x"} size={10} />
                      {PERM_LABELS[k] || k}
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export { Users, Roles };